import { useState, useEffect, useRef, useCallback } from 'react';
import { ModelViewer } from './ModelViewer';
import { SpeechBubble } from './SpeechBubble';
import { useLanguage } from '@/contexts/LanguageContext';
import type { GameState } from '@/game/useTetris';

const ANIMATION_URL = 'https://cdn.jsdelivr.net/gh/Haiko-Innovations/haiko-assets@main/animations/PortfolioAnimation.glb';

interface GameMascotProps {
  gameState: GameState;
  score: number;
  lines: number;
  level: number;
  highScore: number;
}

type MascotMessage = {
  text: string;
};

const mascotTexts = {
  fi: {
    welcome: 'Tervetuloa! 🎮 Kokeile Tetristä ja riko ennätys – voit voittaa alennuskoodin!',
    gameStart: 'Hienoa, peli alkaa! 💪 Onnea matkaan!',
    firstLines: 'Hyvin menee! Jatka samaan malliin! 🔥',
    combo: 'WOW! Mahtava combo! 🎯',
    levelUp: 'Taso nousi! Nyt mennään kovempaa! 🚀',
    nearRecord: 'Olet lähellä ennätystä... Keskity! 👀',
    recordBroken: '🎉 UUSI ENNÄTYS! Mahtavaa pelaamista! Kuponkikoodi tulossa!',
    gameOver: 'Hyvä yritys! Pelaa uudelleen ja tavoittele ennätystä! 💪',
    milestone500: 'Jo 500 pistettä! Olet taitava! ⭐',
    milestone1000: '1000 pistettä! Olet mestari! 🏆',
    encourageIdle: 'Paina Start ja näytä mitä osaat! 🕹️',
    hover: [
      'Hei! 👋 Kokeile peliä!',
      'Uskaltaudutko pelaamaan? 🎮',
      'Ennätys odottaa murtamistaan! 🏆',
      'Voita alennuskoodi! 💰',
    ],
  },
  en: {
    welcome: 'Welcome! 🎮 Try Tetris and break the record – you might win a discount code!',
    gameStart: 'Great, game on! 💪 Good luck!',
    firstLines: 'Nice! Keep it going! 🔥',
    combo: 'WOW! Amazing combo! 🎯',
    levelUp: 'Level up! Things are getting faster! 🚀',
    nearRecord: 'You\'re close to the record... Stay focused! 👀',
    recordBroken: '🎉 NEW RECORD! Amazing play! Coupon code incoming!',
    gameOver: 'Good try! Play again and aim for the record! 💪',
    milestone500: '500 points already! You\'re skilled! ⭐',
    milestone1000: '1000 points! You\'re a master! 🏆',
    encourageIdle: 'Press Start and show what you\'ve got! 🕹️',
    hover: [
      'Hey! 👋 Try the game!',
      'Dare to play? 🎮',
      'The record is waiting to be broken! 🏆',
      'Win a discount code! 💰',
    ],
  },
  sv: {
    welcome: 'Välkommen! 🎮 Prova Tetris och slå rekordet – du kan vinna en rabattkod!',
    gameStart: 'Toppen, spelet börjar! 💪 Lycka till!',
    firstLines: 'Bra jobbat! Fortsätt så! 🔥',
    combo: 'WOW! Fantastisk combo! 🎯',
    levelUp: 'Nivå upp! Nu blir det snabbare! 🚀',
    nearRecord: 'Du är nära rekordet... Fokusera! 👀',
    recordBroken: '🎉 NYTT REKORD! Fantastiskt spel! Kupongkod på väg!',
    gameOver: 'Bra försök! Spela igen och sikta på rekordet! 💪',
    milestone500: '500 poäng redan! Du är skicklig! ⭐',
    milestone1000: '1000 poäng! Du är en mästare! 🏆',
    encourageIdle: 'Tryck Start och visa vad du kan! 🕹️',
    hover: [
      'Hej! 👋 Prova spelet!',
      'Vågar du spela? 🎮',
      'Rekordet väntar på att slås! 🏆',
      'Vinn en rabattkod! 💰',
    ],
  },
  th: {
    welcome: 'ยินดีต้อนรับ! 🎮 ลองเล่น Tetris และทำลายสถิติ – คุณอาจได้รับโค้ดส่วนลด!',
    gameStart: 'เยี่ยม เริ่มเกม! 💪 โชคดี!',
    firstLines: 'เก่งมาก! เล่นต่อไป! 🔥',
    combo: 'ว้าว! คอมโบสุดเจ๋ง! 🎯',
    levelUp: 'เลเวลอัพ! เร็วขึ้นแล้ว! 🚀',
    nearRecord: 'ใกล้สถิติแล้ว... ตั้งสมาธิ! 👀',
    recordBroken: '🎉 สถิติใหม่! เล่นได้เยี่ยมมาก! โค้ดคูปองกำลังมา!',
    gameOver: 'พยายามดี! เล่นอีกครั้งและตั้งเป้าทำลายสถิติ! 💪',
    milestone500: '500 คะแนนแล้ว! คุณเก่งมาก! ⭐',
    milestone1000: '1000 คะแนน! คุณเป็นแชมป์! 🏆',
    encourageIdle: 'กด Start แล้วโชว์ฝีมือ! 🕹️',
    hover: [
      'สวัสดี! 👋 ลองเล่นเกม!',
      'กล้าเล่นไหม? 🎮',
      'สถิติรอให้ทำลาย! 🏆',
      'ชนะโค้ดส่วนลด! 💰',
    ],
  },
} as const;

export function GameMascot({ gameState, score, lines, level, highScore }: GameMascotProps) {
  const { language: lang } = useLanguage();
  const texts = mascotTexts[lang];

  const [message, setMessage] = useState<MascotMessage | null>(null);
  const [bubbleVisible, setBubbleVisible] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const prevGameState = useRef(gameState);
  const prevScore = useRef(score);
  const prevLines = useRef(lines);
  const prevLevel = useRef(level);
  const isShowingMessage = useRef(false);
  const messageQueue = useRef<MascotMessage[]>([]);
  const shownMilestones = useRef(new Set<string>());
  const hoverIndexRef = useRef(0);

  const getModelViewer = useCallback(() => {
    return wrapperRef.current?.querySelector('model-viewer') as any;
  }, []);

  const playAnimationOnce = useCallback(async () => {
    const el = getModelViewer();
    if (!el) return;
    try {
      el.currentTime = 0;
      el.setAttribute('autoplay', '');
      if (el.updateComplete) await el.updateComplete;
      el.play({ repetitions: 1 });
      
      el.addEventListener('finished', () => {
        try {
          el.pause?.();
          el.currentTime = 0;
          el.removeAttribute('autoplay');
        } catch {}
      }, { once: true });
    } catch {}
  }, [getModelViewer]);

  const showMessage = useCallback((msg: MascotMessage, withAnimation = false) => {
    if (isShowingMessage.current) {
      messageQueue.current = [msg];
      return;
    }
    isShowingMessage.current = true;
    setMessage(msg);
    setBubbleVisible(true);

    if (withAnimation) {
      playAnimationOnce();
    }
  }, [playAnimationOnce]);

  const handleBubbleHide = useCallback(() => {
    setBubbleVisible(false);
    isShowingMessage.current = false;
    
    setTimeout(() => {
      if (messageQueue.current.length > 0) {
        const next = messageQueue.current.shift()!;
        showMessage(next);
      }
    }, 600);
  }, [showMessage]);

  // Hover handler – show random hover message and play animation
  const handleMouseEnter = useCallback(() => {
    if (isShowingMessage.current) return;
    const hoverTexts = texts.hover;
    const text = hoverTexts[hoverIndexRef.current % hoverTexts.length];
    hoverIndexRef.current += 1;
    showMessage({ text }, true);
  }, [texts.hover, showMessage]);

  // Welcome message on mount – wait for real language to load
  const welcomeShownForLang = useRef<string | null>(null);
  useEffect(() => {
    // Skip the default 'fi' if real language hasn't loaded yet (first render)
    const timer = setTimeout(() => {
      if (welcomeShownForLang.current === lang) return;
      welcomeShownForLang.current = lang;
      showMessage({ text: texts.welcome });
    }, 1500);
    return () => clearTimeout(timer);
  }, [lang, texts.welcome, showMessage]);

  // Game start – reset milestone tracking, no chatter on start
  useEffect(() => {
    if (prevGameState.current !== 'playing' && gameState === 'playing') {
      shownMilestones.current.clear();
    }
    prevGameState.current = gameState;
  }, [gameState]);

  // Game over
  useEffect(() => {
    if (gameState === 'gameover' && prevGameState.current !== 'gameover') {
      if (score > highScore && highScore > 0) {
        showMessage({ text: texts.recordBroken });
      } else {
        showMessage({ text: texts.gameOver });
      }
    }
  }, [gameState, score, highScore, texts, showMessage]);

  // Score milestones – only every full 1000 points
  useEffect(() => {
    if (gameState !== 'playing') return;
    const prevThousand = Math.floor(prevScore.current / 1000);
    const currThousand = Math.floor(score / 1000);
    if (currThousand > prevThousand && currThousand >= 1 && !shownMilestones.current.has(`k-${currThousand}`)) {
      shownMilestones.current.add(`k-${currThousand}`);
      showMessage({ text: texts.milestone1000 });
    }
    prevScore.current = score;
  }, [score, gameState, texts, showMessage]);

  // Track lines/level silently (no messages)
  useEffect(() => { prevLines.current = lines; }, [lines]);
  useEffect(() => { prevLevel.current = level; }, [level]);

  return (
    <div className="game-mascot">
      <div
        ref={wrapperRef}
        className="game-mascot__animation-wrap"
        onMouseEnter={handleMouseEnter}
      >
        <SpeechBubble
          text={message?.text || ''}
          isVisible={bubbleVisible}
          typingSpeedMs={18}
          autoHide={true}
          onHide={handleBubbleHide}
        />
        <ModelViewer
          src={ANIMATION_URL}
          alt="Haiko mascot"
          className="game-mascot__model"
          autoPlay={false}
          pauseOnFirstFrame={false}
          cameraControls={false}
          disableZoom={true}
          exposure="0.75"
          eager={false}
          onModelRef={(el) => {
            if (!el) return;
            const mv = el as any;
            try {
              mv.currentTime = 0;
              mv.play({ repetitions: 1 });
            } catch {}
          }}
        />
      </div>
    </div>
  );
}
